import React from "react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Plus, Trash2, Bell, Pencil } from "lucide-react";

export function formatBRL(n) {
  return `R$ ${Math.abs(Number(n) || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
export function formatDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}
export function dueState(loan, today) {
  if (!loan.next_due_date || loan.status === "paid") return null;
  if (loan.next_due_date < today) return "late";
  if (loan.next_due_date === today) return "today";
  return null;
}

function MiniStat({ label, value, color }) {
  return (
    <div>
      <div className="text-[0.62rem] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-mono-num text-xs font-bold tabular-nums" style={{ color: color || "#F4F4F5" }}>
        {value}
      </div>
    </div>
  );
}

export const FREQ_LABEL = { weekly: "Semanal", monthly: "Mensal", free: "Livre" };

export default function LoanDetailsDialog({ loan, customer, today, onClose, onAddPayment, onDelete, onEdit }) {
  if (!loan) return null;
  const remaining = Math.max(0, loan.total_receivable - loan.total_paid);
  const paidPct = loan.total_receivable > 0 ? Math.min(100, (loan.total_paid / loan.total_receivable) * 100) : 0;
  const due = dueState(loan, today);

  return (
    <Dialog open={!!loan} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="loan-details-dialog" className="border-white/10 bg-[#121212]">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 font-display text-xl font-bold text-white">
            {customer?.name || "Cliente removido"}
            <span
              className={`rounded-sm px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider ${
                loan.status === "paid" ? "bg-green-500/20 text-green-400" : "bg-orange-500/20 text-orange-400"
              }`}
            >
              {loan.status === "paid" ? "Quitado" : "Ativo"}
            </span>
            <span className="rounded-sm bg-blue-500/20 px-1.5 py-0.5 text-[0.62rem] font-bold uppercase tracking-wider text-blue-400">
              {FREQ_LABEL[loan.frequency] || loan.frequency}
            </span>
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Início {formatDate(loan.start_date)}{loan.first_payment_date && ` · 1º pgto ${formatDate(loan.first_payment_date)}`} · {loan.num_installments}× {formatBRL(loan.installment_value)} · Taxa{" "}
            {loan.interest_rate_percent}% por parcela
            {loan.source && <span className="text-[#22C55E]"> · Caixa: {loan.source}</span>}
          </DialogDescription>
        </DialogHeader>

        {due && (
          <div
            data-testid="loan-due-alert"
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold ${
              due === "late"
                ? "border-red-500/30 bg-red-500/10 text-red-300"
                : "border-[#D4FF00]/30 bg-[#D4FF00]/10 text-[#D4FF00]"
            }`}
          >
            <Bell className="h-3.5 w-3.5" />
            {due === "late" ? "Parcela atrasada desde" : "Parcela vence hoje"} {due === "late" && formatDate(loan.next_due_date)}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 rounded-lg border border-white/10 bg-black/30 p-3 sm:grid-cols-4">
          <MiniStat label="Emprestado" value={formatBRL(loan.principal)} />
          <MiniStat label="Total a receber" value={formatBRL(loan.total_receivable)} color="#D4FF00" />
          <MiniStat label="Recebido (parcelas)" value={formatBRL(loan.total_paid)} color="#00FF66" />
          <MiniStat label="Restante" value={formatBRL(remaining)} color={remaining > 0 ? "#F59E0B" : "#00FF66"} />
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
          <MiniStat label="Parcelas pagas" value={`${loan.installments_paid}/${loan.num_installments}`} />
          <MiniStat label="Próximo vencimento" value={loan.next_due_date ? formatDate(loan.next_due_date) : "—"} />
          <MiniStat label="Juros previstos" value={formatBRL(loan.total_receivable - loan.principal)} color="#A78BFA" />
          {loan.rollovers > 0 && (
            <MiniStat label={`Juros extras (${loan.rollovers}× rolado)`} value={formatBRL(loan.interest_only_total)} color="#A78BFA" />
          )}
        </div>

        <div>
          <div className="h-1.5 overflow-hidden rounded-full bg-white/5">
            <div
              className="h-full rounded-full"
              style={{
                width: `${paidPct}%`,
                backgroundColor: loan.status === "paid" ? "#00FF66" : "#D4FF00",
                transition: "width 0.4s ease",
              }}
            />
          </div>
          <div className="mt-1 text-[0.7rem] text-zinc-600">{paidPct.toFixed(0)}% recebido</div>
        </div>

        {loan.notes && <div className="text-xs text-zinc-400">OBS: {loan.notes}</div>}

        <div>
          <div className="mb-1.5 text-[0.7rem] uppercase tracking-widest text-zinc-500">Recebimentos</div>
          {loan.payments?.length ? (
            <div className="max-h-32 space-y-1 overflow-y-auto pr-1">
              {loan.payments.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-[0.75rem] text-zinc-400">
                  <span className="flex items-center gap-1.5">
                    {formatDate(p.date)}
                    {p.interest_only && (
                      <span className="rounded-sm bg-[#A78BFA]/20 px-1 py-0.5 text-[0.55rem] font-bold uppercase tracking-wider text-[#A78BFA]">Só juros</span>
                    )}
                  </span>
                  <span className="font-mono-num font-semibold" style={{ color: p.interest_only ? "#A78BFA" : "#00FF66" }}>+{formatBRL(p.amount)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[0.75rem] text-zinc-600">Nenhum recebimento registrado.</div>
          )}
        </div>

        <div className="flex gap-2 pt-1">
          {loan.status !== "paid" && (
            <Button
              data-testid={`add-payment-${loan.id}`}
              onClick={() => onAddPayment(loan)}
              className="h-9 flex-1 bg-[#D4FF00] text-xs font-bold text-black hover:bg-[#c4ee00]"
            >
              <Plus className="mr-1.5 h-3 w-3" />
              Registrar recebimento
            </Button>
          )}
          <Button
            data-testid={`edit-loan-${loan.id}`}
            variant="outline"
            onClick={() => onEdit(loan)}
            className="h-9 border-white/10 bg-transparent text-xs text-zinc-300 hover:bg-white/5 hover:text-white"
          >
            <Pencil className="mr-1.5 h-3 w-3" />
            Editar
          </Button>
          <Button
            data-testid={`delete-loan-${loan.id}`}
            variant="outline"
            onClick={() => onDelete(loan)}
            className="h-9 border-white/10 bg-transparent text-xs text-zinc-300 hover:bg-red-500/10 hover:text-red-400"
          >
            <Trash2 className="mr-1.5 h-3 w-3" />
            Excluir
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
